import { useCallback, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Kbd } from '@reveni-io/reveni-ui'

import AppContext from '@/common/context/app.context'

import useGlobalSearch from './hooks/useGlobalSearch'
import useKeyboardNavigation from './hooks/useKeyboardNavigation'
import SearchInput from './SearchInput/SearchInput'
import SearchResults from './SearchResults/SearchResults'
import { getQuickLinks } from './helpers'
import * as Styles from './styles'

const GlobalSearch = ({ show, onClose }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { info } = useContext(AppContext)

  const allowsShipments = info?.selectedStore?.preferences?.allows_shipments
  const allowsUnifiedDashboard = info?.allows_unified_dashboard

  const quickLinks = useMemo(
    () => getQuickLinks({ allowsShipments, allowsUnifiedDashboard }),
    [allowsShipments, allowsUnifiedDashboard]
  )

  const {
    query,
    handleQueryChange,
    shipments,
    returns,
    settings,
    allResults,
    activeIndex,
    setActiveIndex,
    isLoading,
    hasQuery,
    hasResults,
  } = useGlobalSearch({ show })

  const handleSelect = useCallback(
    result => {
      const path = result.path
      if (path) {
        navigate(path)
        onClose()
      }
    },
    [navigate, onClose]
  )

  const currentItems = hasQuery ? allResults : quickLinks

  useKeyboardNavigation({
    show,
    onClose,
    items: currentItems,
    activeIndex,
    setActiveIndex,
    hasQuery,
    onSelect: handleSelect,
  })

  if (!show) return null

  return (
    <Styles.Overlay onClick={onClose} data-testid="global-search-overlay">
      <Styles.Modal onClick={e => e.stopPropagation()} data-testid="global-search-modal">
        <SearchInput value={query} onChange={handleQueryChange} />
        <SearchResults
          settings={settings}
          shipments={shipments}
          returns={returns}
          activeIndex={activeIndex}
          onSelect={handleSelect}
          onHover={setActiveIndex}
          hasQuery={hasQuery}
          hasResults={hasResults}
          isLoading={isLoading}
          quickLinks={quickLinks}
        />
        <Styles.Footer>
          <Styles.FooterHint>
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd>
            {t('Navegar')}
          </Styles.FooterHint>
          <Styles.FooterHint>
            <Kbd>↵</Kbd>
            {t('Seleccionar')}
          </Styles.FooterHint>
          <Styles.FooterHint>
            <Kbd>ESC</Kbd>
            {t('Cerrar')}
          </Styles.FooterHint>
        </Styles.Footer>
      </Styles.Modal>
    </Styles.Overlay>
  )
}

export default GlobalSearch
